/**
 * Bulk grids for the things around a team that are not its games: pools and
 * seeds, the coach contact, and the roster.
 *
 * Nothing is validated here. Every save posts the whole batch to
 * rle-operator-team-grids, which applies all of it or none of it and returns
 * the offending rows by id. This screen collects edits, shows what came back
 * against the right row, and refuses to let a director wander off with unsaved
 * work in the box.
 *
 * Greyed cells are RecruitLook's (team name, club, player names) or belong to
 * another system entirely (payment, registration status). They are not offered,
 * and the function refuses them if they are posted anyway.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Loader2, Save, Table2, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction as rawInvokeEdgeFunction } from '@/lib/invokeEdgeFunction';

/**
 * These grids read `res.ok` / `res.data` straight off the envelope our edge
 * functions return. `invokeEdgeFunction` hands back `{ data, error }`, so the
 * envelope is one level down — reading `res.ok` there is always undefined and
 * every action fails silently as "no draft schedule". Unwrap once, here, and
 * throw on transport errors so the existing catch blocks report them.
 */
async function invokeEdgeFunction(name: string, options?: any): Promise<any> {
  const { data, error } = await rawInvokeEdgeFunction(name, options);
  if (error) throw new Error(error.message || 'Request failed');
  return data;
}

const NONE = '__none__';

type RowError = { id: string; field: string | null; message: string };
type Edits = Record<string, Record<string, any>>;

const FN = 'rle-operator-team-grids';

function useGrid(eventId: string, dataAction: string, saveAction: string, refreshToken?: number) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [edits, setEdits] = useState<Edits>({});
  const [errors, setErrors] = useState<RowError[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await invokeEdgeFunction(FN, { body: { action: dataAction, event_id: eventId } });
      if (!res?.ok) throw new Error(res?.error?.message ?? 'Could not load this grid.');
      setData(res.data);
      setEdits({});
      setErrors([]);
    } catch (e: any) {
      toast.error(e?.message ?? 'Could not load this grid.');
    } finally {
      setLoading(false);
    }
  }, [eventId, dataAction]);

  useEffect(() => { load(); }, [load, refreshToken]);

  const setField = (id: string, field: string, value: any) =>
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] ?? {}), [field]: value } }));

  const dirtyCount = Object.keys(edits).length;

  const save = async () => {
    if (!dirtyCount) return;
    setSaving(true);
    setErrors([]);
    try {
      const payload = Object.entries(edits).map(([id, patch]) => ({ id, ...patch }));
      const res: any = await invokeEdgeFunction(FN, {
        body: { action: saveAction, event_id: eventId, edits: payload },
      });
      if (!res?.ok) {
        setErrors(res?.row_errors ?? []);
        toast.error(res?.error?.message ?? 'Nothing was saved.');
        return;
      }
      toast.success(`Saved ${payload.length} row${payload.length === 1 ? '' : 's'}.`);
      for (const w of (res?.row_warnings ?? []) as Array<{ message: string }>) {
        toast.warning(w.message);
      }
      await load();

    } catch (e: any) {
      toast.error(e?.message ?? 'Nothing was saved.');
    } finally {
      setSaving(false);
    }
  };

  return { loading, data, edits, setField, setEdits, errors, saving, save, load, dirtyCount };
}

function errorsFor(errors: RowError[], id: string) {
  return errors.filter((e) => e.id === id);
}

function SaveBar({ dirtyCount, saving, onSave, onReset, errors }: any) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-t pt-3">
      <Button onClick={onSave} disabled={!dirtyCount || saving} size="sm">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
        Save {dirtyCount || ''} change{dirtyCount === 1 ? '' : 's'}
      </Button>
      <Button variant="ghost" size="sm" onClick={onReset} disabled={!dirtyCount || saving}>
        <Undo2 className="h-4 w-4 mr-2" /> Discard
      </Button>
      {errors.length > 0 && (
        // A count alone is useless: the whole batch is refused, so the reason
        // for each refused row has to be on screen next to the button that
        // was just pressed, not only in the highlighted cell.
        <div className="flex items-start gap-2 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">
              Nothing was saved — {errors.length} row{errors.length === 1 ? '' : 's'}{' '}
              {errors.length === 1 ? 'needs' : 'need'} fixing.
            </div>
            <ul className="mt-1 list-disc space-y-0.5 pl-4 font-normal">
              {errors.map((e: any, i: number) => (
                <li key={`${e.id}-${e.field}-${i}`}>{e.message}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── Teams ─────────────────────────── */
function TeamsGrid({ eventId, refreshToken, division }: { eventId: string; refreshToken?: number; division?: string }) {
  const g = useGrid(eventId, 'teams_data', 'teams_save', refreshToken);
  if (g.loading) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!g.data) return null;

  const val = (t: any, f: string) => (f in (g.edits[t.id] ?? {}) ? g.edits[t.id][f] : t[f]);

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground border-b">
              <th className="py-2 pr-3">Team (RecruitLook)</th>
              <th className="py-2 pr-3">Club</th>
              <th className="py-2 pr-3">Name on this event</th>
              <th className="py-2 pr-3">Division</th>
              <th className="py-2 pr-3">Pool</th>
              <th className="py-2 pr-3">Seed</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Payment</th>
            </tr>
          </thead>
          <tbody>
            {g.data.teams
              .filter((t: any) => !division || (t.division ?? t.age_group) === division)
              .map((t: any) => {
              const rowErrors = errorsFor(g.errors, t.id);
              const bad = (f: string) => rowErrors.some((e) => e.field === f);
              return (
                <tr key={t.id} className={`border-b align-top ${rowErrors.length ? 'bg-destructive/5' : ''}`}>
                  <td className="py-2 pr-3 text-muted-foreground">{t.real_name}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{t.club_name ?? '—'}</td>
                  <td className="py-2 pr-3">
                    <Input
                      className={`h-8 w-48 ${bad('display_name_override') ? 'border-destructive' : ''}`}
                      value={val(t, 'display_name_override') ?? ''}
                      onChange={(e) => g.setField(t.id, 'display_name_override', e.target.value)}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <Select
                      value={val(t, 'division') ?? NONE}
                      onValueChange={(v) => g.setField(t.id, 'division', v === NONE ? null : v)}
                    >
                      <SelectTrigger className={`h-8 w-40 ${bad('division') ? 'border-destructive' : ''}`}>
                        <SelectValue placeholder="Division" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>No division</SelectItem>
                        {g.data.divisions.map((d: string) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-2 pr-3">
                    <Select
                      value={val(t, 'pool_id') ?? NONE}
                      onValueChange={(v) => g.setField(t.id, 'pool_id', v === NONE ? null : v)}
                    >
                      <SelectTrigger className={`h-8 w-36 ${bad('pool_id') ? 'border-destructive' : ''}`}>
                        <SelectValue placeholder="Pool" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>No pool</SelectItem>
                        {g.data.pools
                          // Only this team's own division: offering 17u pools
                          // for a 16u team is an invitation to a save that the
                          // server will refuse anyway.
                          .filter((p: any) => {
                            const d = val(t, 'division');
                            return !d || !p.division || p.division === d;
                          })
                          .map((p: any) => (
                          <SelectItem key={p.id} value={p.id}>{p.division} · {p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-2 pr-3">
                    <Input
                      className={`h-8 w-16 ${bad('seed') ? 'border-destructive' : ''}`}
                      value={val(t, 'seed') ?? ''}
                      onChange={(e) => g.setField(t.id, 'seed', e.target.value)}
                    />
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">{t.registration_status ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{t.payment_status ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {g.errors.length > 0 && (
        <ul className="text-sm text-destructive space-y-1">
          {g.errors.map((e, i) => <li key={i}>• {e.message}</li>)}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        Status and payment are shown for scanning only. Payment comes from the checkout, and withdrawing a
        team has its own action because it moves pools, games and brackets.
      </p>
      <SaveBar dirtyCount={g.dirtyCount} saving={g.saving} errors={g.errors}
        onSave={g.save} onReset={() => { g.setEdits({}); }} />
    </div>
  );
}

/* ─────────────────────────── Contacts ─────────────────────────── */
function ContactsGrid({ eventId, refreshToken, division }: { eventId: string; refreshToken?: number; division?: string }) {
  const g = useGrid(eventId, 'contacts_data', 'contacts_save', refreshToken);
  if (g.loading) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!g.data) return null;

  const val = (t: any, f: string) => (f in (g.edits[t.id] ?? {}) ? g.edits[t.id][f] : t[f]);
  const fields = [
    ['coach_first_name', 'First name'],
    ['coach_last_name', 'Last name'],
    ['coach_email', 'Email'],
    ['coach_phone', 'Phone'],
  ] as const;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground border-b">
              <th className="py-2 pr-3">Team</th>
              {fields.map(([, label]) => <th key={label} className="py-2 pr-3">{label}</th>)}
              <th className="py-2 pr-3">Claimed</th>
            </tr>
          </thead>
          <tbody>
            {g.data.teams
              .filter((t: any) => !division || (t.division ?? t.age_group) === division)
              .map((t: any) => {
              const rowErrors = errorsFor(g.errors, t.id);
              return (
                <tr key={t.id} className={`border-b ${rowErrors.length ? 'bg-destructive/5' : ''}`}>
                  <td className="py-2 pr-3 text-muted-foreground">{t.real_name}</td>
                  {fields.map(([f]) => (
                    <td key={f} className="py-2 pr-3">
                      <Input
                        className={`h-8 w-44 ${rowErrors.some((e) => e.field === f) ? 'border-destructive' : ''}`}
                        value={val(t, f) ?? ''}
                        onChange={(e) => g.setField(t.id, f, e.target.value)}
                      />
                    </td>
                  ))}
                  <td className="py-2 pr-3">
                    {t.coach_claimed
                      ? <Badge variant="secondary">Claimed</Badge>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {g.errors.length > 0 && (
        <ul className="text-sm text-destructive space-y-1">
          {g.errors.map((e, i) => <li key={i}>• {e.message}</li>)}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        Whose login is attached to a team is not editable here — changing it would hand one coach another
        team's data. Use the claim and invitation flow for that.
      </p>
      <SaveBar dirtyCount={g.dirtyCount} saving={g.saving} errors={g.errors}
        onSave={g.save} onReset={() => g.setEdits({})} />
    </div>
  );
}

/* ─────────────────────────── Roster ─────────────────────────── */
function RosterGrid({ eventId, refreshToken }: { eventId: string; refreshToken?: number }) {
  const g = useGrid(eventId, 'roster_data', 'roster_save', refreshToken);
  const [team, setTeam] = useState<string>(NONE);
  if (g.loading) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!g.data) return null;

  const val = (p: any, f: string) => (f in (g.edits[p.id] ?? {}) ? g.edits[p.id][f] : p[f]);
  const rows = g.data.players.filter((p: any) => team === NONE || p.registration_id === team);

  return (
    <div className="space-y-3">
      <Select value={team} onValueChange={setTeam}>
        <SelectTrigger className="h-8 w-64"><SelectValue placeholder="All teams" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>All teams</SelectItem>
          {g.data.teams.map((t: any) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground border-b">
              <th className="py-2 pr-3">Team</th>
              <th className="py-2 pr-3">Player (RecruitLook)</th>
              <th className="py-2 pr-3">Grad</th>
              <th className="py-2 pr-3">Jersey</th>
              <th className="py-2 pr-3">Position</th>
              <th className="py-2 pr-3">Captain</th>
              <th className="py-2 pr-3">Guest</th>
              <th className="py-2 pr-3">Remove</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p: any) => {
              const rowErrors = errorsFor(g.errors, p.id);
              const removing = val(p, 'remove') === true;
              return (
                <tr key={p.id} className={`border-b ${rowErrors.length ? 'bg-destructive/5' : ''} ${removing ? 'opacity-50' : ''}`}>
                  <td className="py-2 pr-3 text-muted-foreground">{p.team_name}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{p.player_name}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{p.grad_year ?? '—'}</td>
                  <td className="py-2 pr-3">
                    <Input
                      className={`h-8 w-16 ${rowErrors.some((e) => e.field === 'jersey_number') ? 'border-destructive' : ''}`}
                      value={val(p, 'jersey_number') ?? ''}
                      onChange={(e) => g.setField(p.id, 'jersey_number', e.target.value)}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <Input
                      className="h-8 w-24"
                      value={val(p, 'position') ?? ''}
                      onChange={(e) => g.setField(p.id, 'position', e.target.value)}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <Checkbox checked={!!val(p, 'is_captain')}
                      onCheckedChange={(v) => g.setField(p.id, 'is_captain', v === true)} />
                  </td>
                  <td className="py-2 pr-3">
                    <Checkbox checked={!!val(p, 'is_guest_player')}
                      onCheckedChange={(v) => g.setField(p.id, 'is_guest_player', v === true)} />
                  </td>
                  <td className="py-2 pr-3">
                    <Button variant="ghost" size="sm"
                      onClick={() => g.setField(p.id, 'remove', !removing)}>
                      <Trash2 className={`h-4 w-4 ${removing ? 'text-destructive' : ''}`} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {g.errors.length > 0 && (
        <ul className="text-sm text-destructive space-y-1">
          {g.errors.map((e, i) => <li key={i}>• {e.message}</li>)}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        Names and grad years belong to the athlete's RecruitLook profile. Adding a new athlete stays with the
        roster upload, so a typo in a cell can never create a duplicate athlete.
      </p>
      <SaveBar dirtyCount={g.dirtyCount} saving={g.saving} errors={g.errors}
        onSave={g.save} onReset={() => g.setEdits({})} />
    </div>
  );
}

export default function TeamDataGrids({ eventId, refreshToken, division }: { eventId: string; refreshToken?: number; division?: string }) {
  const [tab, setTab] = useState('teams');
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Table2 className="h-4 w-4" /> Bulk grids{division ? ` — ${division}` : ''}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="teams">Teams</TabsTrigger>
            <TabsTrigger value="contacts">Team contacts</TabsTrigger>
            <TabsTrigger value="roster">Athletes</TabsTrigger>
          </TabsList>
          <TabsContent value="teams" className="pt-4">
            <TeamsGrid eventId={eventId} refreshToken={refreshToken} division={division} />
          </TabsContent>
          <TabsContent value="contacts" className="pt-4">
            <ContactsGrid eventId={eventId} refreshToken={refreshToken} division={division} />
          </TabsContent>
          <TabsContent value="roster" className="pt-4">
            <RosterGrid eventId={eventId} refreshToken={refreshToken} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
